/**
 * Absence Mapper
 * 
 * Transforms backend DTOs (LC_*) to UI models
 */

import { formatPersonName } from '@/utils';
import type {
  LC_AbsenceItemOut,
  LC_ListAbsencesOut,
  LC_AbsenceScopeItemOut,
  LC_ListAbsencesByScopeListOut,
  LC_EmployeeForAbsenceItemOut,
  LC_GetEmployeesForAbsenceOut,
  LC_GetMedicalLeaveDetailOut,
  AbsenceItem,
  AbsenceListResult,
  AbsenceTotals,
  AbsenceScopeItem,
  AbsenceScopeListResult,
  AbsenceType,
  EmployeeForAbsence,
  EmployeesForAbsenceResult,
  MedicalLeaveDetail,
} from './absence.types';

/**
 * Format date string from ISO to dd/mm/yyyy
 */
const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return '-';
  try {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

/**
 * Replace null or empty string with '-'
 */
const replaceEmpty = (value: string | null | undefined): string => {
  if (!value || value.trim() === '') return '-';
  return value;
};

/**
 * Map single absence item from backend to UI model
 */
export const mapAbsenceItem = (item: LC_AbsenceItemOut): AbsenceItem => ({
  id: replaceEmpty(item.id),
  tipo: replaceEmpty(item.type),
  descripcion: replaceEmpty(item.description),
  fechaDesde: formatDate(item.startDate),
  fechaHasta: formatDate(item.endDate),
  estado: replaceEmpty(item.status),
  fechaCreacion: formatDate(item.createdAt),
  creadoPor: replaceEmpty(item.createdBy),
});

/**
 * Extract totals from pagination metadata
 */
export const mapAbsenceTotals = (pagination: LC_ListAbsencesOut['pagination']): AbsenceTotals => ({
  licencias: pagination.totalMedicalLeaves,
  vacaciones: pagination.totalVacations,
  permisosEspeciales: pagination.totalSpecialPermits,
  permisosAdministrativos: pagination.totalAdministrativePermits,
  permisosFraccion: pagination.totalFractionPermits,
  faltas: pagination.totalAbsences,
});

/**
 * Map full absence list response to UI result
 */
export const mapAbsenceListResponse = (response: LC_ListAbsencesOut): AbsenceListResult => ({
  data: (response.data ?? []).map(mapAbsenceItem),
  page: response.pagination.currentPage,
  pageSize: response.pagination.pageSize,
  totalRecords: response.pagination.totalRecords,
  totalPages: response.pagination.totalPages,
  totals: mapAbsenceTotals(response.pagination),
});

/**
 * Convierte el id crudo del listado (string, potencialmente '') a un PK numérico dedicado.
 * `item.id` pasa por `replaceEmpty` en `mapAbsenceItem` y termina en '-' cuando viene vacío,
 * así que no puede reutilizarse para navegar al detalle — de ahí este campo separado.
 */
const toRecordId = (id: string | null): number | null => {
  if (!id) return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Map single absence scope item (adds employee fields to base mapping)
 */
export const mapAbsenceScopeItem = (item: LC_AbsenceScopeItemOut): AbsenceScopeItem => ({
  ...mapAbsenceItem(item),
  empleado: replaceEmpty(item.employeeName),
  rut: replaceEmpty(item.employeeTaxId),
  typeCode: (item.typeCode || 'todos') as AbsenceType,
  recordId: toRecordId(item.id),
});

/**
 * Map full absence scope list response to UI result
 */
export const mapAbsenceScopeListResponse = (
  response: LC_ListAbsencesByScopeListOut,
): AbsenceScopeListResult => ({
  data: (response.data ?? []).map(mapAbsenceScopeItem),
  page: response.pagination.currentPage,
  pageSize: response.pagination.pageSize,
  totalRecords: response.pagination.totalRecords,
  totalPages: response.pagination.totalPages,
  totals: mapAbsenceTotals(response.pagination),
});

/**
 * Map single employee available for absence creation.
 * El backend ya devuelve fullName como "ApellidoPaterno ApellidoMaterno Nombres";
 * aquí sólo se normaliza el caso.
 */
export const mapEmployeeForAbsenceItem = (
  item: LC_EmployeeForAbsenceItemOut,
): EmployeeForAbsence => ({
  id: item.id,
  employeeTaxIdentificationNumber: item.employeeTaxIdentificationNumber ?? '',
  fullName: formatPersonName(item.fullName ?? ''),
  companyName: item.companyName ?? '',
  isBlocked: item.isBlocked,
});

/**
 * Map full employees-for-absence response to UI result
 */
export const mapEmployeesForAbsenceResponse = (
  response: LC_GetEmployeesForAbsenceOut,
): EmployeesForAbsenceResult => ({
  data: (response.data ?? []).map(mapEmployeeForAbsenceItem),
  page: response.pagination.currentPage,
  pageSize: response.pagination.pageSize,
  totalRecords: response.pagination.totalRecords,
  totalPages: response.pagination.totalPages,
  hasNextPage: response.pagination.hasNextPage,
});

/**
 * Recorta una fecha ISO a `yyyy-MM-dd` sin construir un `Date` — evita el corrimiento por
 * zona horaria que produciría `formatDate` (además de que ese helper devuelve dd/mm/yyyy,
 * incompatible con el regex del schema de edición y con `DatePicker`).
 */
const toIsoDate = (value: string | null): string => (value ? value.slice(0, 10) : '');

/**
 * Map del detalle de una licencia médica (backend → modelo UI del panel de ver/editar).
 * No reutiliza `replaceEmpty`: metería un '-' literal en `folio`, que es editable y se
 * guardaría tal cual si el usuario no lo toca.
 */
export const mapMedicalLeaveDetail = (out: LC_GetMedicalLeaveDetailOut): MedicalLeaveDetail => ({
  id: out.id,
  employeeTargetId: out.employeeTargetId,
  employeeName: formatPersonName(out.employeeName ?? ''),
  employeeTaxId: out.employeeTaxId ?? '',
  leaveTypeId: out.leaveTypeId,
  folio: out.folio ?? '',
  workdayType: out.workdayType,
  dayCount: out.dayCount,
  startDate: toIsoDate(out.startDate),
  endDate: toIsoDate(out.endDate),
  returnDate: toIsoDate(out.returnDate),
  isManagedByPayroll: out.isManagedByPayroll,
});
